/**
 * Der Agentenschritt im Prozess-Designer.
 *
 * Tritt an die Stelle des Prompt-Feldes, wenn als Schritt-Typ „Einen Agenten losschicken" gewählt
 * ist. Die Reiter für Ein- und Ausgabe bleiben unberührt — ein Agent bekommt dieselben Parameter
 * und wird gegen dasselbe Ausgabeschema geprüft wie die anderen Schritt-Typen.
 *
 * **Die Werkzeugliste kommt aus dem Register des Servers**, nicht aus einer Liste hier. Wer ein
 * Werkzeug registriert und es in der Auswahl nicht findet, soll am Server suchen und nicht hier.
 *
 * Gerechnet und geprüft wird in {@link ./agentStepConfig}; dieses Panel zeigt nur. Was hier an
 * Logik stünde, wäre nicht prüfbar.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Search } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  BUDGET_LIMITS,
  GOAL_MAX_LENGTH,
  ROUTING_ROWS,
  behaviourOf,
  budgetErrors,
  msToSeconds,
  requiresHitlRole,
  secondsToMs,
  toggleTool,
  toggleToolGroup,
  gruppenAuswahl,
  gruppenPolitik,
  setzeGruppenPolitik,
  validateAgentConfig,
  type AgentStepConfig,
  type CatalogueTool,
} from "./agentStepConfig";

interface Properties {
  config: AgentStepConfig;
  onChange: (config: AgentStepConfig) => void;
  /** Die Werkzeuge aus dem Register. `null`, solange sie geholt werden. */
  tools: CatalogueTool[] | null;
  /** Ob das Laden des Katalogs fehlgeschlagen ist. */
  catalogueError?: boolean;
  /**
   * Ob Pflichthinweise (z. B. „Ohne Ziel lässt sich der Schritt nicht speichern“) sofort erscheinen.
   * Der Assistent zum Anlegen schaltet sie ab, bis jemand weiter will — ein leeres Formular soll
   * nicht gleich rot sein.
   */
  showValidation?: boolean;
}

/** Two-way choice for what a tool may do without asking — a segmented control instead of two loose buttons. */
function PolicyToggle<T extends string>({
  value,
  options,
  onChange,
  label,
  className = "",
}: {
  /** The current choice; anything not among the options (e.g. "gemischt" for a mixed group) marks none. */
  value: string | null | undefined;
  options: Array<{ value: T; text: string }>;
  onChange: (value: T) => void;
  label: string;
  className?: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={`flex shrink-0 gap-0.5 rounded-lg bg-muted p-0.5 text-xs ${className}`}
    >
      {options.map(option => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          className={`rounded-md px-2.5 py-1 font-medium transition-colors ${
            value === option.value
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => onChange(option.value)}
        >
          {option.text}
        </button>
      ))}
    </div>
  );
}

export default function AgentStepPanel({ config, onChange, tools, catalogueError, showValidation = true }: Properties) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  /** Welcher Reiter offen ist. Werkzeuge zuerst — dort fängt jede Einrichtung an. */
  const [reiter, setReiter] = useState("werkzeuge");

  const fehler = budgetErrors(config);
  const pruefung = validateAgentConfig(config);
  const rolleNoetig = requiresHitlRole(config);

  /** Gefiltert über Beschriftung, Name und Beschreibung — man hat mal das eine, mal das andere im Kopf. */
  const gefiltert = useMemo(() => {
    const suche = search.trim().toLowerCase();
    const liste = tools ?? [];
    if (!suche) return liste;
    return liste.filter(tool =>
      [tool.label, tool.name, tool.description].some(text =>
        String(text ?? "").toLowerCase().includes(suche),
      ),
    );
  }, [tools, search]);

  /** Nach Herkunft gruppiert, Reihenfolge wie geliefert — der Server sortiert bereits. */
  const nachKategorie = useMemo(() => {
    const gruppen = new Map<string, CatalogueTool[]>();
    for (const tool of gefiltert) {
      const vorhanden = gruppen.get(tool.category);
      if (vorhanden) vorhanden.push(tool);
      else gruppen.set(tool.category, [tool]);
    }
    return Array.from(gruppen.entries());
  }, [gefiltert]);

  const budgetFeld = (
    feld: "maxTurns" | "maxTotalTokens",
    grenzen: { min: number; max: number },
    label: string,
  ) => (
    <div className="min-w-0 flex-1">
      <Label htmlFor={`agent-${feld}`}>{label}</Label>
      <Input
        id={`agent-${feld}`}
        type="number"
        min={grenzen.min}
        max={grenzen.max}
        value={config.budget[feld]}
        aria-invalid={fehler[feld]}
        className={fehler[feld] ? "border-destructive" : ""}
        onChange={event =>
          onChange({
            ...config,
            budget: { ...config.budget, [feld]: Number(event.target.value) },
          })
        }
      />
      {fehler[feld] && (
        <div className="mt-1 text-xs text-destructive">
          {t("ui.agentStep.outOfRange", { min: grenzen.min, max: grenzen.max })}
        </div>
      )}
    </div>
  );

  /** Sekunden im Feld, Millisekunden im Datensatz — die Umrechnung steht nur in `agentStepConfig`. */
  const sekundenFeld = (
    feld: "wallClockMs" | "toolCallTimeoutMs",
    grenzen: { min: number; max: number },
    label: string,
  ) => (
    <div className="min-w-0 flex-1">
      <Label htmlFor={`agent-${feld}`}>{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          id={`agent-${feld}`}
          type="number"
          min={grenzen.min}
          max={grenzen.max}
          value={msToSeconds(config.budget[feld])}
          aria-invalid={fehler[feld]}
          className={fehler[feld] ? "border-destructive" : ""}
          onChange={event =>
            onChange({
              ...config,
              budget: { ...config.budget, [feld]: secondsToMs(Number(event.target.value)) },
            })
          }
        />
        <span className="shrink-0 text-xs text-muted-foreground">{t("ui.agentStep.seconds")}</span>
      </div>
      {fehler[feld] && (
        <div className="mt-1 text-xs text-destructive">
          {t("ui.agentStep.outOfRange", { min: grenzen.min, max: grenzen.max })}
        </div>
      )}
    </div>
  );

  return (
    <div className="flex w-full min-w-0 flex-col gap-6">
      {/*
        * **Vier Reiter statt einer langen Bahn.**
        *
        * Der Schritt trägt fünf Abschnitte; untereinander gestellt reichte er weit unter den
        * sichtbaren Bereich, und was dort steht, findet niemand: Die Freigaberolle wurde von einem
        * Klick oben in der Werkzeugliste verlangt und stand unten hinter der Ausgangstabelle.
        *
        * Das **Ziel** bleibt über den Reitern. Es ist der Kern des Schrittes, es ist kurz, und es
        * ist das Einzige, ohne das sich nichts speichern lässt — es zu verstecken hiesse, die
        * wichtigste Angabe hinter einen Klick zu legen.
        *
        * **Ein Punkt am Reiter zeigt, wo etwas fehlt.** Sonst wandert das alte Problem nur um:
        * Wer speichern will und abgewiesen wird, muss sehen, in welchem Reiter die Ursache sitzt.
        */}
          {/* ---------------------------------------------------------------- Ziel */}
          <div>
            <div className="mb-2 text-sm font-bold">{t("ui.agentStep.goal")}</div>
            <Textarea
              aria-label={t("ui.agentStep.goal")}
              /*
               * Sechzehn Zeilen, nicht acht: Das Ziel ist der Text, an dem hier wirklich
               * geschrieben wird — bei acht Zeilen scrollt man in einem Feld, das selbst in ein
               * Fenster gehört. Zu den Reitern darunter muss man ohnehin scrollen, also kostet die
               * Höhe hier nichts, was sie nicht zurückgibt.
               */
              rows={8}
              value={config.goal}
              /* Kein `maxLength`: Es kürzt eingefügten Text still — siehe GOAL_MAX_LENGTH. */
              placeholder={t("ui.agentStep.goalPlaceholder")}
              className="bg-background text-sm"
              onChange={event => onChange({ ...config, goal: event.target.value })}
            />
            <div className="mt-1 flex justify-between text-xs text-muted-foreground">
              <span className={showValidation && pruefung.blocking.includes("goalEmpty") ? "text-destructive" : ""}>
                {showValidation && pruefung.blocking.includes("goalEmpty") ? t("ui.agentStep.goalRequired") : ""}
              </span>
              <span className={config.goal.length > GOAL_MAX_LENGTH ? "text-destructive" : ""}>
                {config.goal.length} / {GOAL_MAX_LENGTH}
              </span>
            </div>
          </div>


      <Tabs value={reiter} onValueChange={setReiter} className="w-full min-w-0">
        <TabsList>
          <TabsTrigger value="werkzeuge">
            {t("ui.agentStep.tools")}
            {pruefung.blocking.includes("hitlRoleMissing") && (
              <span aria-hidden className="ml-1.5 h-1.5 w-1.5 rounded-full bg-destructive" />
            )}
          </TabsTrigger>
          <TabsTrigger value="budget">
            {t("ui.agentStep.budget")}
            {pruefung.blocking.includes("budgetOutOfRange") && (
              <span aria-hidden className="ml-1.5 h-1.5 w-1.5 rounded-full bg-destructive" />
            )}
          </TabsTrigger>
          <TabsTrigger value="ergebnis">{t("ui.agentStep.result")}</TabsTrigger>
          <TabsTrigger value="ausgaenge">{t("ui.agentStep.routing")}</TabsTrigger>
        </TabsList>

        <TabsContent value="werkzeuge" className="mt-4 flex min-w-0 flex-col gap-6">
          {/* ----------------------------------------------------------- Werkzeuge */}
          <div>
            <div className="mb-3 flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
              <div className="min-w-0">
                <div className="text-sm font-medium">{t("ui.agentStep.tools")}</div>
                <p className="mt-0.5 text-sm text-muted-foreground">{t("ui.agentStep.toolsHelp")}</p>
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-foreground">
                  {t("ui.agentStep.toolsSelected", {
                    selected: config.tools.allow.length,
                    total: tools?.length ?? 0,
                  })}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto px-1 py-1 font-normal text-foreground underline underline-offset-4 hover:bg-transparent disabled:no-underline disabled:opacity-40"
                  disabled={!config.tools.allow.length}
                  onClick={() => onChange({ ...config, tools: { allow: [], policy: {} } })}
                >
                  {t("ui.agentStep.deselectAll")}
                </Button>
              </div>
            </div>

            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="h-10 pl-9"
                value={search}
                placeholder={t("ui.agentStep.searchTools")}
                onChange={event => setSearch(event.target.value)}
              />
            </div>

            {catalogueError ? (
              <div className="rounded-md border border-destructive bg-destructive/10 p-3 text-xs text-destructive">
                {t("ui.agentStep.toolsLoadError")}
              </div>
            ) : tools === null ? (
              <div className="rounded-md border p-3 text-xs text-muted-foreground">
                {t("ui.agentStep.toolsLoading")}
              </div>
            ) : tools.length === 0 ? (
              // Ein leeres Register ist kein Fehler des Formulars — der Rest bleibt bedienbar.
              <div className="rounded-md border p-3 text-xs text-muted-foreground">
                {t("ui.agentStep.noToolsRegistered")}
              </div>
            ) : (
              <div className="max-h-[440px] overflow-y-auto rounded-xl border bg-background">
                {nachKategorie.map(([kategorie, gruppe]) => (
                  <div key={kategorie}>
                    {/*
                      * Der ganze Kasten auf einmal: Bei 301 Werkzeugen ist „alles aus tri-assets"
                      * sonst vierzig Klicks. Das Kästchen zeigt den Stand der Gruppe — leer, teilweise
                      * (waagerechter Strich), voll — und setzt beim Klick alle Werkzeuge der Gruppe
                      * über dieselbe Regel, die auch ein Einzelklick nimmt.
                      */}
                    <div className="sticky top-0 z-[1] flex items-center gap-2 border-b bg-muted/90 px-4 py-2 text-sm font-medium backdrop-blur">
                      <Checkbox
                        id={`agent-toolset-${kategorie}`}
                        aria-label={t("ui.agentStep.selectToolset", { toolset: kategorie })}
                        checked={
                          gruppenAuswahl(config, gruppe) === "alle"
                            ? true
                            : gruppenAuswahl(config, gruppe) === "teilweise"
                              ? "indeterminate"
                              : false
                        }
                        onCheckedChange={checked =>
                          onChange(toggleToolGroup(config, gruppe, checked === true))
                        }
                      />
                      <Label htmlFor={`agent-toolset-${kategorie}`} className="cursor-pointer text-sm font-medium">
                        {kategorie}
                      </Label>
                      <span className="text-xs font-normal text-muted-foreground">
                        {t("ui.agentStep.toolsSelected", {
                          selected: gruppe.filter(werkzeug => config.tools.allow.includes(werkzeug.name)).length,
                          total: gruppe.length,
                        })}
                      </span>
                      {/*
                        * Die Erlaubnis für den ganzen Satz — dieselbe Wahl wie je Werkzeug, nur einmal
                        * für alle ausgewählten. Sie erscheint erst mit einer Auswahl: Für ein nicht
                        * ausgewähltes Werkzeug gibt es keine Erlaubnis zu setzen.
                        */}
                      {gruppenPolitik(config, gruppe) !== null && (
                        <PolicyToggle
                          className="ml-auto"
                          value={gruppenPolitik(config, gruppe)}
                          label={t("ui.agentStep.policyLabel")}
                          options={[
                            { value: "auto", text: t("ui.agentStep.policy.auto") },
                            { value: "approve", text: t("ui.agentStep.policy.approve") },
                          ]}
                          onChange={politik => onChange(setzeGruppenPolitik(config, gruppe, politik))}
                        />
                      )}
                    </div>
                    {gruppe.map(tool => {
                      const gewaehlt = config.tools.allow.includes(tool.name);
                      return (
                        <div
                          key={tool.name}
                          className={`group flex items-start gap-3 border-b px-4 py-3 last:border-b-0 transition-colors ${
                            gewaehlt ? "bg-primary/5" : "hover:bg-muted/40"
                          }`}
                        >
                          <Checkbox
                            id={`agent-tool-${tool.name}`}
                            className="mt-0.5"
                            checked={gewaehlt}
                            onCheckedChange={checked =>
                              onChange(toggleTool(config, tool, checked === true))
                            }
                          />
                          <div className="min-w-0 flex-1">
                            <Label
                              htmlFor={`agent-tool-${tool.name}`}
                              className="flex cursor-pointer flex-wrap items-center gap-x-2 gap-y-1 text-sm font-medium"
                            >
                              {tool.label}
                              {tool.sideEffects && (
                                <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs font-normal text-amber-700">
                                  {t("ui.agentStep.hasSideEffects")}
                                </span>
                              )}
                            </Label>
                            {tool.description && (
                              <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground" title={tool.description}>
                                {tool.description}
                              </p>
                            )}
                            {/*
                              * Der technische Name erscheint erst beim Darüberfahren oder Fokussieren:
                              * Er taucht im Protokoll, im BPMN und in jeder Fehlermeldung auf — wer
                              * eine Auswahl gegen einen Lauf hält, braucht ihn —, aber er soll die
                              * Liste für alle anderen nicht übertönen. Er steht im Dokument, also
                              * auch für Screenreader und die Suche; weggelassen nur, wo er die
                              * Beschriftung wiederholt.
                              */}
                            {tool.name !== tool.label && (
                              <code className="mt-1 block font-mono text-xs text-muted-foreground opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
                                {tool.name}
                              </code>
                            )}
                          </div>
                          {/* Die Erlaubnis erscheint erst mit der Auswahl — „verboten" ist die
                              Abwesenheit des Hakens, kein dritter Zustand daneben. */}
                          {gewaehlt && (
                            <PolicyToggle
                              value={config.tools.policy[tool.name]}
                              label={t("ui.agentStep.policyLabel")}
                              options={[
                                { value: "auto", text: t("ui.agentStep.policy.auto") },
                                { value: "approve", text: t("ui.agentStep.policy.approve") },
                              ]}
                              onChange={policy =>
                                onChange({
                                  ...config,
                                  tools: {
                                    ...config.tools,
                                    policy: { ...config.tools.policy, [tool.name]: policy },
                                  },
                                })
                              }
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}

            {pruefung.warnings.includes("noTools") && (
              <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-600">
                <AlertTriangle className="h-3.5 w-3.5" />
                {t("ui.agentStep.noToolsWarning")}
              </div>
            )}
          </div>

          {/*
            * **Die Freigaberolle steht bei der Auswahl, die sie verlangt.**
            *
            * Sie stand bis zum 18.09.2026 ganz am Ende des Formulars — hinter Budget, Ergebnis und
            * der Ausgangstabelle, also ausserhalb des Bildes. Ausgelöst wird sie aber von einem Klick
            * in der Werkzeugliste: Der Nutzer drückte oben auf Speichern, und die Antwort auf die
            * Frage „warum nicht" lag unten. Jetzt steht sie direkt unter der Liste.
            */}
          {/* ------------------------------------------------------------- Freigabe */}
          {rolleNoetig && (
            <div>
              <div className="mb-2 text-sm font-bold">{t("ui.agentStep.hitlRole")}</div>
              <Input
                value={config.hitlRole ?? ""}
                placeholder={t("ui.agentStep.hitlRolePlaceholder")}
                aria-invalid={!config.hitlRole}
                className={!config.hitlRole ? "border-destructive" : ""}
                onChange={event => onChange({ ...config, hitlRole: event.target.value || undefined })}
              />
              {!config.hitlRole && (
                <div className="mt-1 text-xs text-destructive">{t("ui.agentStep.hitlRoleRequired")}</div>
              )}
            </div>
          )}

        </TabsContent>

        <TabsContent value="budget" className="mt-4 flex min-w-0 flex-col gap-6">
          {/* -------------------------------------------------------------- Budget */}
          <div>
            <div className="mb-2 text-sm font-bold">{t("ui.agentStep.budget")}</div>
            <div className="flex flex-wrap gap-4">
              {budgetFeld("maxTurns", BUDGET_LIMITS.maxTurns, t("ui.agentStep.maxTurns"))}
              {budgetFeld("maxTotalTokens", BUDGET_LIMITS.maxTotalTokens, t("ui.agentStep.maxTokens"))}
              {sekundenFeld("wallClockMs", BUDGET_LIMITS.wallClockSeconds, t("ui.agentStep.wallClock"))}
              {sekundenFeld(
                "toolCallTimeoutMs",
                BUDGET_LIMITS.toolCallTimeoutSeconds,
                t("ui.agentStep.toolTimeout"),
              )}
            </div>
          </div>

        </TabsContent>

        <TabsContent value="ergebnis" className="mt-4 flex min-w-0 flex-col gap-6">
          {/* ------------------------------------------------------------ Ergebnis */}
          <div>
            <div className="mb-2 text-sm font-bold">{t("ui.agentStep.result")}</div>
            {/* Beide Schalter in einer Zeile: Es sind zwei Entscheidungen über dieselbe Sache — was mit
                dem Ergebnis geschieht —, und untereinander gestellt lasen sie sich wie zwei Abschnitte. */}
            <div className="flex flex-wrap gap-x-10 gap-y-3">
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <Switch
                  id="agent-allow-partial"
                  checked={config.output.allowPartial}
                  onCheckedChange={checked =>
                    onChange({ ...config, output: { ...config.output, allowPartial: checked } })
                  }
                />
                <div>
                  <Label htmlFor="agent-allow-partial" className="cursor-pointer font-normal">
                    {t("ui.agentStep.allowPartial")}
                  </Label>
                  <div className="text-xs text-muted-foreground">
                    {t("ui.agentStep.allowPartialHelp")}
                  </div>
                </div>
              </div>
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <Switch
                  id="agent-require-provenance"
                  checked={config.output.requireProvenance}
                  onCheckedChange={checked =>
                    onChange({ ...config, output: { ...config.output, requireProvenance: checked } })
                  }
                />
                <div>
                  <Label htmlFor="agent-require-provenance" className="cursor-pointer font-normal">
                    {t("ui.agentStep.requireProvenance")}
                  </Label>
                  <div className="text-xs text-muted-foreground">
                    {t("ui.agentStep.requireProvenanceHelp")}
                  </div>
                </div>
              </div>
            </div>
          </div>

        </TabsContent>

        <TabsContent value="ausgaenge" className="mt-4 flex min-w-0 flex-col gap-6">
          {/* ------------------------------------------------------------- Ausgänge */}
          <div>
            <div className="mb-1 text-sm font-bold">{t("ui.agentStep.routing")}</div>
            {/* Der Satz steht hier, weil sonst jeder das Ziel an dieser Stelle sucht: Ein Prompt-Tool
                wird von mehreren Workflows benutzt, und wohin es weitergeht, gehört dem Prozess. */}
            <p className="mb-3 text-xs text-muted-foreground">{t("ui.agentStep.routingExplained")}</p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">{t("ui.agentStep.routingExit")}</TableHead>
                  <TableHead className="w-[260px]">{t("ui.agentStep.routingBehaviour")}</TableHead>
                  <TableHead className="w-[120px]">{t("ui.agentStep.routingRetries")}</TableHead>
                  <TableHead>{t("ui.agentStep.routingStatusCode")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ROUTING_ROWS.filter(
                  // Ohne Teilergebnisse gibt es keinen Ausgang dafür — eine Zeile, die nichts
                  // entscheiden kann, verlangt sonst eine Entscheidung.
                  row => row.status !== "partial" || config.output.allowPartial,
                ).map(row => {
                  const eintrag = config.routing?.[row.status] ?? {};
                  const meldung = behaviourOf(config, row.status);
                  return (
                    <TableRow key={row.status}>
                      <TableCell className="align-middle">
                        {t(`ui.agentStep.exit.${row.status}`)}
                      </TableCell>
                      <TableCell>
                        {row.fixed ? (
                          // Festgelegt, nicht gesperrt-aussehend: „Erfolgreich" IST ein Erfolg. Eine
                          // Auswahl anzubieten, die nur einen sinnvollen Wert hat, ist eine Scheinfrage.
                          <span className="text-muted-foreground">
                            {t(`ui.agentStep.behaviour.${meldung}`)}
                          </span>
                        ) : (
                          <div className="flex overflow-hidden rounded-md border text-xs">
                            {(["success", "error", "ask"] as const).map(wert => (
                              <button
                                key={wert}
                                type="button"
                                className={`flex-1 px-2 py-1 ${
                                  meldung === wert
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-background"
                                }`}
                                onClick={() =>
                                  onChange({
                                    ...config,
                                    routing: {
                                      ...config.routing,
                                      [row.status]: { ...eintrag, behaviour: wert },
                                    },
                                  })
                                }
                              >
                                {t(`ui.agentStep.behaviour.${wert}`)}
                              </button>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.retries !== undefined && (
                          <Input
                            type="number"
                            min={0}
                            max={5}
                            value={eintrag.retries ?? row.retries}
                            onChange={event =>
                              onChange({
                                ...config,
                                routing: {
                                  ...config.routing,
                                  [row.status]: { ...eintrag, retries: Number(event.target.value) },
                                },
                              })
                            }
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {/* Die Vokabel zum Anknüpfen im BPMN. Ohne sie muss man sie raten. */}
                        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                          {row.status === "failed" ? "failed / aborted" : row.status}
                        </code>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/**
 * Den Werkzeugkatalog holen — einmal je Montage des Panels.
 *
 * Absichtlich nicht gecacht: Der Katalog soll den Stand des Registers zeigen, und ein Neuladen ist
 * der Weg, ein frisch registriertes Werkzeug zu sehen.
 */
export const useToolCatalogue = (
  fetcher: (locale: string) => Promise<CatalogueTool[]>,
  locale: string,
): { tools: CatalogueTool[] | null; error: boolean } => {
  const [tools, setTools] = useState<CatalogueTool[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let abgebrochen = false;
    setTools(null);
    setError(false);
    fetcher(locale)
      .then(liste => {
        if (!abgebrochen) {
          setTools(liste);
          setError(false);
        }
      })
      .catch(err => {
        console.warn("Werkzeugkatalog konnte nicht geladen werden", err);
        if (!abgebrochen) {
          setTools(null);
          setError(true);
        }
      });
    return () => {
      abgebrochen = true;
    };
  }, [fetcher, locale]);

  return { tools, error };
};
